/**
 * PublicLinkButton
 * ================
 * A small dropdown/dialog that lets ops/admin generate, copy, regenerate, and
 * revoke a public tracking link for a shipment. Shown inside ShipmentDetailPage
 * next to the document-generation buttons.
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  getPublicTrackingLink, generatePublicTrackingLink, revokePublicTrackingLink,
} from '../lib/api';
import { toast } from 'sonner';
import {
  Link2, Copy, RefreshCw, Power, Loader2, ExternalLink, ShieldCheck,
} from 'lucide-react';

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

export function PublicLinkButton({ mbl, isReadOnly }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);  // { enabled, token, created_at, expires_at, revoked_at }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadState = async () => {
    setLoading(true);
    try {
      const r = await getPublicTrackingLink(mbl);
      setState(r.data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mbl]);

  const publicUrl = state?.token
    ? `${window.location.origin}/track/${state.token}`
    : null;

  const handleGenerate = async () => {
    // If a link already exists, confirm before rotating — the old URL becomes
    // invalid immediately, so anyone who has it loses access.
    const isActive = state?.enabled && state?.token;
    if (isActive && !window.confirm(
      '¿Regenerar el link? La URL actual quedará inválida inmediatamente y cualquier persona que la tenga dejará de poder ver el seguimiento.',
    )) {
      return;
    }
    setBusy(true);
    try {
      const r = await generatePublicTrackingLink(mbl);
      setState(r.data);
      toast.success(isActive ? 'Link regenerado — URL anterior invalidada' : 'Link público generado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar link');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('¿Revocar el link público? Quien tenga la URL dejará de poder ver el seguimiento.')) return;
    setBusy(true);
    try {
      const r = await revokePublicTrackingLink(mbl);
      setState(r.data);
      toast.success('Link público revocado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al revocar');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar. Selecciona y copia manualmente.');
    }
  };

  // Hide button completely for client/read-only viewers
  if (isReadOnly) return null;

  const active = state?.enabled && state?.token;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-public-link-btn"
        title="Compartir link público de tracking"
      >
        <Link2 size={16} className="mr-2" />
        Link público
        {active && (
          <span className="ml-2 inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="public-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 size={18} /> Link público de tracking
            </DialogTitle>
            <DialogDescription>
              Genera una URL compartible para que tu cliente o chofer vea el seguimiento de
              este embarque <strong className="font-mono">{mbl}</strong> sin necesidad de iniciar sesión.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                {active ? (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    <ShieldCheck size={12} className="mr-1" /> Activo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">Inactivo</Badge>
                )}
                {state?.created_at && (
                  <span className="text-xs text-muted-foreground">
                    Generado: {formatDate(state.created_at)}
                  </span>
                )}
              </div>

              {/* URL display */}
              {active && (
                <>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">URL pública</label>
                    <div className="flex gap-2">
                      <Input
                        value={publicUrl}
                        readOnly
                        className="font-mono text-xs"
                        data-testid="public-link-url-input"
                        onFocus={(e) => e.target.select()}
                      />
                      <Button onClick={handleCopy} variant="outline" size="icon" title="Copiar" data-testid="copy-public-link-btn">
                        <Copy size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => window.open(publicUrl, '_blank', 'noopener')}
                        title="Abrir en pestaña nueva"
                        data-testid="open-public-link-btn-external"
                      >
                        <ExternalLink size={14} />
                      </Button>
                    </div>
                  </div>

                  {state?.expires_at && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 text-xs">
                      <span className="text-amber-300 font-medium">Expira automáticamente:</span>{' '}
                      <span className="text-muted-foreground">{formatDate(state.expires_at)} (30 días post-entrega)</span>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>• El destinatario verá: timeline, ETA, status del embarque y contenedores.</p>
                    <p>• <strong>NO</strong> verá: costos, notas internas, ni datos de cliente/proveedor.</p>
                    <p>• Puedes revocar el link en cualquier momento.</p>
                  </div>
                </>
              )}

              {!active && state?.revoked_at && (
                <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-sm p-3 text-xs">
                  <span className="text-muted-foreground">
                    Link revocado el {formatDate(state.revoked_at)}.
                    Genera uno nuevo para reactivar el seguimiento público.
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border/50">
                {!active ? (
                  <Button onClick={handleGenerate} disabled={busy} data-testid="generate-public-link-btn">
                    {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Link2 size={14} className="mr-2" />}
                    Generar link público
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleGenerate} variant="outline" disabled={busy} data-testid="regenerate-public-link-btn" title="Invalida el link actual y genera uno nuevo">
                      <RefreshCw size={14} className="mr-2" />
                      Regenerar (invalida actual)
                    </Button>
                    <Button onClick={handleRevoke} variant="outline" disabled={busy} className="text-destructive hover:text-destructive" data-testid="revoke-public-link-btn">
                      <Power size={14} className="mr-2" />
                      Revocar
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
